// String + label formatters shared by the card views (positions / orders /
// trades).

import { describeMarket, truncateTicker } from '../../lib/kalshiTicker/describeMarket';
import { roundToCentiCents } from '../../lib/pricing/subCent';

/** Price label — grain-canonical, trimmed ("56.9¢", "58.25¢", "55¢").
 *  The old Math.round displayed deci_cent order/position prices as
 *  integers on every card surface — prod-caught 2026-07-17. Volumes stay
 *  integer-rounded (fmtVol); quantities round only when integral (fmtQty);
 *  prices carry the 0.01¢ grain. */
export function fmtCents(c: number | null | undefined): string {
  return c != null && Number.isFinite(c) ? `${roundToCentiCents(c)}¢` : '—';
}

/** Quantity label (#144). Contract/share counts render as WHOLE numbers on every
 *  user-facing card + dashboard surface — Kalshi (integer-native) and Polymarket
 *  (fractional shares) alike. Display-only: the exact underlying quantity is never
 *  mutated, and accounting tables / CSV exports keep full precision (they do not
 *  use this formatter). Tradeoff accepted by #144: a Polymarket card's count now
 *  reads whole next to its exact `$1 × count` payout (supersedes the 2026-07-22
 *  2-decimal display). */
export function fmtQty(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return Math.round(n).toLocaleString('en-US');
}

export function fmtUsd(d: number | null | undefined): string {
  if (d == null || !Number.isFinite(d)) return '—';
  return `$${d.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Signed dollars — "+$3.05" / "-$1.20". */
export function fmtUsdSigned(d: number | null | undefined): string {
  if (d == null || !Number.isFinite(d)) return '—';
  const sign = d >= 0 ? '+' : '-';
  return `${sign}$${Math.abs(d).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Compact elapsed age — "8m", "6h", "3d". Empty string when unknown. */
export function fmtAge(
  sinceMs: number | null | undefined,
  nowMs: number = Date.now(),
): string {
  if (sinceMs == null || !Number.isFinite(sinceMs)) return '';
  const s = Math.max(0, Math.floor((nowMs - sinceMs) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

/** Exact local timestamp for an aggregate's most recent fill. The optional
 * time zone exists for deterministic tests; production intentionally uses the
 * browser's local zone. Invalid/missing inputs remain unknown. */
export function fmtLastFillAt(
  value: string | null | undefined,
  timeZone?: string,
): string {
  if (!value) return '—';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...(timeZone ? { timeZone } : {}),
  }).format(date);
}

/** Compact signed integer for velocity figures — "+1.4k", "-72k", "+340". */
export function fmtCompactSigned(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const sign = n < 0 ? '-' : '+';
  const abs = Math.abs(n);
  let body: string;
  if (abs >= 1000) {
    const k = abs / 1000;
    body = `${k >= 100 ? Math.round(k) : k.toFixed(1).replace(/\.0$/, '')}k`;
  } else {
    body = `${Math.round(abs)}`;
  }
  return `${sign}${body}`;
}

/** Split describeMarket's "MATCHUP — SPORT — DATE" string into a card title
 *  (matchup) plus a subtitle (sport · date · truncated ticker). Non-game
 *  tickers fall back to the truncated ticker as the title.
 *
 *  `team` and `titleTeam` are the market's named side for its TWO different
 *  jobs, and they are not always the same string:
 *
 *    team      → the standalone "(NYC) YES" side badge. Stays a short code.
 *    titleTeam → the token `BetTeamTitle` highlights INSIDE `title`, which for
 *                a feed-named matchup reads "New York City", not "NYC".
 *
 *  They were one field until #685 gave soccer cards the club names the
 *  dashboard row already showed; a whole-token search for the code then found
 *  nothing in the title and the highlight silently disappeared. */
export function titleParts(
  ticker: string,
  side: 'yes' | 'no',
): { title: string; subtitle: string; team: string | null; titleTeam: string | null } {
  const { marketLabel, betTeam, titleTeam } = describeMarket(ticker, side);
  const [head, ...rest] = marketLabel.split(' — ');
  const subtitle = [...rest, truncateTicker(ticker)].join(' · ');
  return { title: head || marketLabel, subtitle, team: betTeam, titleTeam };
}

/** Contract volume — "1.2M / 12.3K / 999". Mirrors the ladder's
 *  formatVolume (Math.round before stringifying: Kalshi volumes are
 *  floats and sub-1k fractions must never render raw). Lives here so
 *  components/ don't import from views/. */
export function fmtVol(vol: number | null | undefined): string {
  if (vol == null || !Number.isFinite(vol) || vol === 0) return '—';
  if (vol >= 1_000_000) return `${(vol / 1_000_000).toFixed(1)}M`;
  if (vol >= 10_000) return `${(vol / 1_000).toFixed(1)}K`;
  const rounded = Math.round(vol);
  if (rounded >= 1_000) return rounded.toLocaleString();
  return String(rounded);
}
